import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Button } from '../ui/Button';
import { Notice, StatusBadge } from '../ui/Badge';
import { Field, field } from '../create/Field';
import { EmptyState } from '../ui/EmptyState';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { IconFleet } from '../icons';
import { dateOnly } from '../../lib/format';

/**
 * Which VAN this tool rides in, and which vans it rode in before (114 part 6).
 *
 * 🔴 **This is not the same question as "who runs it".** A tool has TWO holders and nothing
 * reconciles them: a person, through `asset_assignments`, and a van, through
 * `asset_placements`. **Placement answers "what is on this van"; assignment answers "who is
 * responsible."** A shop really does hand a tech a van full of somebody else's tools, so the
 * two lists sit in separate cards under separate headings and are never merged — merging
 * them would force one of the two facts to be wrong.
 *
 * ⭐ Mirrors `AssignmentEditor` deliberately, down to rendering the server's refusal
 * UNCHANGED. A clash comes back as *"It is already on 'Van 3' for that period — take it off
 * that van first"*, which names the van standing in the way; "Could not save" throws away
 * the one fact needed to fix it.
 */
export type Placement = {
  id: string;
  vehicleAssetId: string;
  vehicleName: string;
  startsOn: string;
  endsOn: string | null;
};

/** Where a stint sits relative to the SHOP's today. */
const phaseOf = (p: Placement, today: string): 'past' | 'current' | 'upcoming' =>
  p.startsOn > today ? 'upcoming' : !p.endsOn || p.endsOn >= today ? 'current' : 'past';

export function PlacementEditor({
  assetId,
  assetStatus,
  today,
}: {
  assetId: string;
  assetStatus: string;
  today: string;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const placements = useQuery(trpc.asset.placements.queryOptions({ assetId }));
  /**
   * The vans to choose from.
   *
   * ⚠️ `kind: 'vehicle'` in the QUERY, not filtered here. The server refuses a non-vehicle
   * target outright, so a picker offering tools would be offering options guaranteed to come
   * back refused — and a control that can only fail reads as a bug in the product rather
   * than as a rule.
   */
  const vans = useQuery(
    trpc.asset.list.queryOptions({ kind: 'vehicle', limit: 100, offset: 0 }),
  );
  const [vehicleAssetId, setVehicleAssetId] = useState('');
  const [startsOn, setStartsOn] = useState(today);
  const [endsOn, setEndsOn] = useState('');
  const [ending, setEnding] = useState<{ id: string; date: string } | null>(null);

  const done = () => void qc.invalidateQueries();
  const put = useMutation(
    trpc.asset.placementSet.mutationOptions({
      onSuccess: () => {
        setVehicleAssetId('');
        setEndsOn('');
        done();
      },
    }),
  );
  const end = useMutation(
    trpc.asset.placementEnd.mutationOptions({
      onSuccess: () => {
        setEnding(null);
        done();
      },
    }),
  );

  const retired = assetStatus === 'retired';
  const rows = placements.data ?? [];
  // ⚠️ Both queries are gated, not `?? []`. The van list IS the control, and the placement
  // list is the answer — rendering either failure as "nothing here" is the honesty failure
  // `QueryStates` exists to prevent: an empty list and a broken query look identical.
  const listState = shownState(placements, placements.data);
  const vanState = shownState(vans, vans.data);
  // A retired van cannot take equipment (the server refuses), so it is not offered.
  const vanOptions = (vans.data?.rows ?? []).filter((v) => v.status !== 'retired');

  if (!placements.data) {
    return (
      <UnresolvedState
        state={unresolved(listState)}
        what="where this is kept"
        icon={IconFleet}
        error={placements.error}
        onRetry={() => void placements.refetch()}
        skeleton="h-24"
      />
    );
  }

  return (
    <div className="space-y-4">
      {put.error ? <Notice tone="danger">{put.error.message}</Notice> : null}
      {end.error ? <Notice tone="danger">{end.error.message}</Notice> : null}
      <StaleNotice
        show={refreshFailed(placements, placements.data)}
        onRetry={() => void placements.refetch()}
      />

      {rows.length === 0 ? (
        <EmptyState
          icon={IconFleet}
          title="Not on a van"
          description="Put it on the van that carries it and it shows up in that rig's kit on the dispatch board."
        />
      ) : (
        <ul className="divide-y divide-border text-sm">
          {rows.map((p) => {
            const phase = phaseOf(p, today);
            return (
              <li key={p.id} className="space-y-2 py-2.5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {p.vehicleName}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {dateOnly(p.startsOn)} →{' '}
                    {p.endsOn ? dateOnly(p.endsOn) : 'no end date'}
                  </span>
                  {phase === 'current' ? (
                    <StatusBadge tone="success">On board</StatusBadge>
                  ) : phase === 'upcoming' ? (
                    <StatusBadge tone="info">Upcoming</StatusBadge>
                  ) : (
                    <StatusBadge tone="neutral">Past</StatusBadge>
                  )}
                  {/* A past stint has nothing left to end and a retired asset's history is
                      frozen — the server refuses both, so neither gets a button. */}
                  {!retired && phase !== 'past' ? (
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      onClick={() =>
                        setEnding(
                          ending?.id === p.id
                            ? null
                            : {
                                id: p.id,
                                date: phase === 'upcoming' ? p.startsOn : today,
                              },
                        )
                      }
                    >
                      {ending?.id === p.id ? 'Cancel' : 'Take off'}
                    </Button>
                  ) : null}
                </div>
                {ending?.id === p.id ? (
                  <div className="flex flex-wrap items-end gap-2 rounded-[var(--radius)] bg-surface-2 p-2">
                    <Field label="Last day on board">
                      <input
                        type="date"
                        className={field}
                        value={ending.date}
                        min={p.startsOn}
                        onChange={(e) => setEnding({ id: p.id, date: e.target.value })}
                      />
                    </Field>
                    <Button
                      disabled={!ending.date || end.isPending}
                      onClick={() =>
                        end.mutate({ placementId: p.id, endsOn: ending.date, ...idem() })
                      }
                    >
                      {end.isPending ? 'Saving…' : 'Take it off'}
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {retired ? (
        <p className="text-xs text-muted-foreground">
          Retired assets keep their history and go on no new vans. Put it back in service
          first.
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-2 border-t border-border pt-4">
          {!vans.data ? (
            <div className="w-full">
              <UnresolvedState
                state={unresolved(vanState)}
                what="the shop's vans"
                icon={IconFleet}
                error={vans.error}
                onRetry={() => void vans.refetch()}
                skeleton="h-10"
              />
            </div>
          ) : (
            <>
              {/*
               * ⚠️ Caught by `settings-states.test.ts`, not by me: the van list DECIDES what
               * the picker offers, so a refresh that failed would leave a stale roster on
               * screen saying nothing — a van added five minutes ago simply absent, with no
               * hint that the list is old. Same rule `AssignmentEditor` follows for its
               * member roster.
               */}
              <StaleNotice
                show={refreshFailed(vans, vans.data)}
                onRetry={() => void vans.refetch()}
              />
              <div className="min-w-[12rem] flex-1">
                <Field label="Put it on">
                  <select
                    className={field}
                    value={vehicleAssetId}
                    onChange={(e) => setVehicleAssetId(e.target.value)}
                  >
                    <option value="">Choose a van…</option>
                    {vanOptions.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                        {v.plate ? ` · ${v.plate}` : ''}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="From">
                <input
                  type="date"
                  className={field}
                  value={startsOn}
                  onChange={(e) => setStartsOn(e.target.value)}
                />
              </Field>
              <Field label="Until (optional)">
                <input
                  type="date"
                  className={field}
                  value={endsOn}
                  min={startsOn}
                  onChange={(e) => setEndsOn(e.target.value)}
                />
              </Field>
              <Button
                disabled={!vehicleAssetId || !startsOn || put.isPending}
                onClick={() =>
                  put.mutate({
                    assetId,
                    vehicleAssetId,
                    startsOn,
                    ...(endsOn ? { endsOn } : {}),
                    ...idem(),
                  })
                }
              >
                {put.isPending ? 'Saving…' : 'Put it on'}
              </Button>
              {/* ⚠️ Says what the server actually does. Moving a tool between vans is TWO
                  acts — take it off one, put it on the next — because `placementSet` never
                  ends an existing stint and the one-live-van guard refuses the overlap.
                  Promising an automatic handoff and then refusing it is worse than asking
                  for the extra click, because the refusal then reads as a bug. */}
              <p className="w-full text-xs text-muted-foreground">
                A tool is on one van at a time. To move it, take it off the current van
                first — the same day counts as an overlap. Leave &ldquo;until&rdquo; empty
                for the normal case.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
